'use client';

import { useEffect } from 'react';
import { usePreservedCallback } from '@toss/react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import '../../subscription/hooks/usePayple/events.web-payple';
import { ZepApiError, ZepApiStatusType } from '@zep/apis';
import { useDialog } from '@zep/dialog';
import { trackEvent } from '@zep/logger/tracking';
import { PlanStatus } from '@zep/types';
import { isMobileDevice } from '@zep/utils';
import { usePlanSpaceDetail, useRemovePaymentCard, useSellingSpacePlanTemplates } from '../../../../services';
import { OperationType } from '../../../../types';
import { PlanPaymentClientError } from '../../subscription';
import { PlanTemplateType } from '../types';
import { createInitialProviderProps } from '../utils';
import { ForeignPaymentContent } from './ForeignPaymentContent';
import { KrPlanPaymentContent } from './KrPlanPaymentContent';
import { PlanPaymentLoading } from './PlanPaymentLoading';
import { PlanPaymentInfoProvider, PlanPaymentInfoProviderProps } from './Providers';
import { PlanPaymentFormProvider } from './Providers';

/**
 * 결제페이지의 첫 진입점으로 결제에 필요한 데이터를 fetching하고 Provider로 내려준다.
 * 결제페이지 Form에 필요한 defaultValues를 내려주는 역할도 한다.
 * 전달받은 props를 통해 가공해서 사용하고 싶은 정보가 있다면 이곳에서 내려준다.
 * 결제 페이지에서는 결제하려는 space의 countryCode에 따라 KrPaymentProvider와 ForeignPaymentProvider로 나뉜다.
 * 결제하려는 플랜이 가상계좌 결제 대기 중 상태라면 결제 대기 중 페이지로 이동한다.
 * @constructor
 */
export const PlanPaymentSection = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    query,
    replace
  } = useRouter();
  const spaceHashId = (query.spaceHashId || '').toString();
  const code = (query.code || '').toString();
  const message = (query.message || '').toString();
  const dialog = useDialog();
  const OnetimePaymentFailAlert = usePreservedCallback(({
    message
  }: {
    code: string;
    message: string;
  }) => {
    void dialog.alert({
      title: t('subscription:payment.alertFailPay.title'),
      content: t('subscription:SpaceDetail.changeCardModal.reason', {
        defaultValue: '사유 : {{message}} 계속 문제가 발생한다면 카드를 다시 등록해 주시거나 홈페이지를 통해 문의해 주세요.',
        message
      })
    });
  });
  const {
    data: planPaymentInfo
  } = usePlanSpaceDetail(spaceHashId);
  const {
    data: subscriptionPlanTemplates
  } = useSellingSpacePlanTemplates({
    spaceHashId,
    templateType: PlanTemplateType.PRODUCT_SUBSCRIPTION
  }, {
    enabled: true
  });
  const {
    data: onetimePlanTemplates
  } = useSellingSpacePlanTemplates({
    spaceHashId,
    templateType: PlanTemplateType.PRODUCT_ONETIME
  }, {
    enabled: true
  });
  const {
    mutateAsync: removePaymentCard
  } = useRemovePaymentCard();
  const readyPlans = planPaymentInfo?.readySpacePlans;
  const isKrPayment = planPaymentInfo?.space.countryCode === 'KR';
  const formProviderProps = createInitialProviderProps(query, planPaymentInfo);
  const InfoProviderProps: PlanPaymentInfoProviderProps = {
    subscriptionPlanTemplates,
    onetimePlanTemplates,
    isKrPayment,
    spaceHashId,
    planPaymentInfo
  };
  useEffect(() => {
    // 기존 결제 페이지 로직 재사용
    // 실제 플랜이 없고 스페이스 빌링키만 잇을 경우 beforeunload 시점에서 삭제해서 실수 할 가능성을 줄이는 코드
    const removeCard = () => {
      if (!planPaymentInfo?.spacePlan && planPaymentInfo?.billingInfo) {
        void removePaymentCard(spaceHashId);
      }
    };
    window.addEventListener('beforeunload', removeCard);
    return () => {
      window.removeEventListener('beforeunload', removeCard);
    };
  }, [removePaymentCard, spaceHashId, planPaymentInfo?.spacePlan, planPaymentInfo?.billingInfo]);
  useEffect(() => {
    // 재결제 시도가 있을 경우 결제 실패 페이지로 이동
    if (planPaymentInfo?.spacePlan?.paymentFailCount && planPaymentInfo.spacePlan.paymentFailCount > 0) {
      throw new ZepApiError(ZepApiStatusType.FAILED, 'payment_status_fail', 'payment_status_fail', 'payment_status_fail');
    }
    if (isMobileDevice()) {
      throw new PlanPaymentClientError('MOBILE_DEVICE_NOT_SUPPORTED');
    }

    // 상품 일시납/커스텀 일시납 레디 플랜이 있을 경우
    if (planPaymentInfo?.readySpacePlans?.length > 0) {
      const hasVirtualAccountInfo = planPaymentInfo?.readySpacePlans?.some(plan => plan.virtualAccountInfo != null);
      if (!hasVirtualAccountInfo) {
        throw new PlanPaymentClientError('ALREADY_ONETIME_READY_PLAN_EXISTS');
      }
    }

    // 현재 플랜이 커스텀 일시납인 경우
    if (planPaymentInfo?.spacePlan?.operationType === OperationType.커스텀_일시납 && planPaymentInfo.spacePlan.status !== PlanStatus.EXPIRED) {
      throw new PlanPaymentClientError('ALREADY_CUSTOM_ONETIME_PLAN_EXISTS');
    }

    // active enterprise 플랜 존재
    if (planPaymentInfo?.spacePlan?.planType === 'ENTERPRISE' && planPaymentInfo.spacePlan.status !== PlanStatus.EXPIRED) {
      throw new PlanPaymentClientError('ALREADY_ACTIVE_ENTERPRISE_PLAN_EXISTS');
    }
  }, [planPaymentInfo?.readySpacePlans, planPaymentInfo?.spacePlan?.operationType, planPaymentInfo?.spacePlan?.paymentFailCount, planPaymentInfo?.spacePlan?.planType, planPaymentInfo?.spacePlan?.status, planPaymentInfo?.subscription?.planType]);
  useEffect(() => {
    if (code || message) {
      trackEvent('web_payple_result_fail', {
        code,
        message,
        order_id: (query.orderId || '').toString(),
        space_hash_id: spaceHashId
      });
      OnetimePaymentFailAlert({
        code,
        message
      });

      // 현재 query를 복사한 후 code와 message만 제거
      const updatedQuery = {
        ...query
      };
      delete updatedQuery.code;
      delete updatedQuery.message;

      // 쿼리 파라미터에서 code와 message를 제거하고 나머지 쿼리는 유지한 채로 URL을 업데이트
      void replace({
        pathname: window.location.pathname,
        // 현재 경로 유지
        query: updatedQuery // code와 message가 제거된 쿼리
      }, undefined, {
        shallow: true
      } // shallow를 사용해 페이지 리로딩 없이 URL 변경
      );
    }
  }, [OnetimePaymentFailAlert, code, message, query, replace, spaceHashId]);

  // 가상계좌 입금 대기중인 경우 pending-deposit 페이지로 이동
  if (readyPlans && readyPlans[0]?.virtualAccountInfo) {
    void replace(`/plan-payment/onetime/${readyPlans[0]?.readySpacePlan?.spacePlanHashId}/pending-deposit`);
    return null;
  }
  return <PlanPaymentFormProvider {...formProviderProps} data-sentry-element="PlanPaymentFormProvider" data-sentry-component="PlanPaymentSection" data-sentry-source-file="PlanPaymentSection.tsx">
      <PlanPaymentInfoProvider {...InfoProviderProps} data-sentry-element="PlanPaymentInfoProvider" data-sentry-source-file="PlanPaymentSection.tsx">
        <PlanPaymentLoading data-sentry-element="PlanPaymentLoading" data-sentry-source-file="PlanPaymentSection.tsx" />
        {isKrPayment ? <KrPlanPaymentContent /> : <ForeignPaymentContent />}
      </PlanPaymentInfoProvider>
    </PlanPaymentFormProvider>;
};